import { adminFetch } from '../../lib/adminFetch';
import React, { useState, useEffect, useRef, useCallback } from 'react';

const LogoAdmin = () => {
  const [items, setItems] = useState([]);
  const [message, setMessage] = useState('');
  const fileInput = useRef(null);
  const [dragIdx, setDragIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);

  // モーダル
  const [modalOpen, setModalOpen] = useState(false);
  const [modalIdx, setModalIdx] = useState(null);
  const [modalName, setModalName] = useState('');
  const [modalLogoUrl, setModalLogoUrl] = useState('');

  useEffect(() => {
    adminFetch('/api/admin-read.php?file=logo-list&t=' + Date.now())
      .then((r) => r.ok ? r.json().catch(() => []) : [])
      .then((d) => setItems(Array.isArray(d) ? d : []))
      .catch(() => setItems([]));
  }, []);

  // サーバーに即保存
  const persist = useCallback(async (newItems) => {
    try {
      const res = await adminFetch('/api/admin-data.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ file: 'logo-list', data: newItems }),
      });
      const data = await res.json();
      if (data.success) {
        setMessage('保存しました');
        setTimeout(() => setMessage(''), 2000);
      } else {
        setMessage('保存に失敗しました');
      }
    } catch {
      setMessage('保存に失敗しました');
    }
  }, []);

  // モーダル
  const openNew = () => {
    setModalIdx(null);
    setModalName('');
    setModalLogoUrl('');
    setModalOpen(true);
  };

  const openEdit = (i) => {
    setModalIdx(i);
    setModalName(items[i].name);
    setModalLogoUrl(items[i].logoUrl || '');
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setModalIdx(null);
  };

  const saveModal = () => {
    let newItems;
    if (modalIdx === null) {
      newItems = [...items, { id: Date.now(), name: modalName, logoUrl: modalLogoUrl }];
    } else {
      newItems = items.map((item, i) =>
        i === modalIdx ? { ...item, name: modalName, logoUrl: modalLogoUrl } : item
      );
    }
    setItems(newItems);
    persist(newItems);
    closeModal();
  };

  const handleUploadInModal = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('files[]', file);
    try {
      const res = await adminFetch('/api/admin-files.php?action=upload', { method: 'POST', body: formData });
      const data = await res.json();
      if (data.success) {
        setModalLogoUrl('/uploads/' + file.name);
      }
    } catch { /* */ }
    if (fileInput.current) fileInput.current.value = '';
  };

  const remove = (i) => {
    if (!window.confirm('削除しますか？')) return;
    const newItems = items.filter((_, idx) => idx !== i);
    setItems(newItems);
    persist(newItems);
  };

  // ドラッグ
  const handleDragStart = (i) => setDragIdx(i);
  const handleDragOver = (e, i) => { e.preventDefault(); setDragOverIdx(i); };
  const handleDragEnd = () => {
    if (dragIdx !== null && dragOverIdx !== null && dragIdx !== dragOverIdx) {
      const newItems = [...items];
      const [moved] = newItems.splice(dragIdx, 1);
      newItems.splice(dragOverIdx, 0, moved);
      setItems(newItems);
      persist(newItems);
    }
    setDragIdx(null);
    setDragOverIdx(null);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">パートナーロゴ管理</h2>
        <button onClick={openNew} className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-bold hover:bg-blue-700">
          + 追加
        </button>
      </div>

      <p className="text-sm text-gray-500 mb-4">
        トップページの「信頼できるパートナー事務所」に表示されるロゴを管理します。ドラッグで並べ替え、クリックで編集できます。<br />
        ロゴ画像がない場合は名前がテキストで表示されます。変更は即時保存されます。
      </p>

      {/* プレビュー */}
      {items.length > 0 && (
        <div className="mb-6 bg-white rounded-xl shadow p-4">
          <p className="text-xs font-bold text-gray-400 mb-2">プレビュー（トップページでの表示イメージ）</p>
          <div className="overflow-hidden rounded-lg" style={{ background: 'linear-gradient(135deg, #fafafa, #f0f0f0)' }}>
            <div className="flex py-3 overflow-x-auto">
              {items.map((item, i) => (
                <div key={i} className="flex-shrink-0 mx-2 bg-white rounded-lg shadow-sm p-2 w-40 h-16 flex items-center justify-center">
                  {item.logoUrl ? (
                    <img src={item.logoUrl} alt={item.name} className="max-w-full max-h-full object-contain" />
                  ) : (
                    <span className="text-center font-bold leading-tight px-1" style={{ fontSize: (item.name || '').length > 12 ? '10px' : '12px', color: '#403737' }}>
                      {item.name || '(未設定)'}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {message && <div className="mb-4 px-4 py-2 bg-green-100 text-green-800 rounded-lg text-sm">{message}</div>}

      <div className="space-y-2">
        {items.length === 0 && (
          <div className="bg-white rounded-xl shadow p-8 text-center text-gray-500">データがありません。「+ 追加」で作成してください。</div>
        )}
        {items.map((item, i) => (
          <div
            key={item.id || i}
            draggable
            onDragStart={() => handleDragStart(i)}
            onDragOver={(e) => handleDragOver(e, i)}
            onDragEnd={handleDragEnd}
            className={`bg-white rounded-xl shadow p-3 flex items-center gap-4 transition-all cursor-grab active:cursor-grabbing ${
              dragIdx === i ? 'opacity-50 scale-95' : ''
            } ${dragOverIdx === i && dragIdx !== i ? 'border-2 border-blue-400' : 'border-2 border-transparent'}`}
          >
            <div className="w-28 h-14 bg-gray-50 rounded border flex items-center justify-center shrink-0 overflow-hidden">
              {item.logoUrl ? (
                <img src={item.logoUrl} alt={item.name} className="max-w-full max-h-full object-contain" />
              ) : (
                <span className="text-[11px] font-bold text-gray-600 text-center leading-tight px-1">
                  {item.name || '(未設定)'}
                </span>
              )}
            </div>

            <div className="flex-1 min-w-0">
              <p className="font-bold text-sm truncate">{item.name || '(名前未設定)'}</p>
            </div>

            <div className="flex items-center gap-2 shrink-0">
              <button onClick={() => openEdit(i)} className="bg-gray-100 text-gray-700 px-3 py-1.5 rounded-lg text-xs font-bold hover:bg-gray-200">編集</button>
              <button onClick={() => remove(i)} className="bg-red-50 text-red-600 px-3 py-1.5 rounded-lg text-xs font-bold hover:bg-red-100">削除</button>
            </div>
          </div>
        ))}
      </div>

      {/* 編集モーダル */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}>
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <h3 className="font-bold text-lg mb-4">{modalIdx === null ? '新規追加' : '編集'}</h3>

            <div className="mb-4">
              <label className="block text-sm font-bold mb-1">名前</label>
              <input
                type="text"
                value={modalName}
                onChange={(e) => setModalName(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                autoFocus
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-bold mb-1">ロゴ画像</label>
              {modalLogoUrl ? (
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-32 h-16 bg-gray-50 rounded border flex items-center justify-center overflow-hidden">
                    <img src={modalLogoUrl} alt="" className="max-w-full max-h-full object-contain" />
                  </div>
                  <button onClick={() => setModalLogoUrl('')} className="text-xs text-red-500 hover:text-red-700">削除</button>
                </div>
              ) : (
                <p className="text-xs text-gray-400 mb-2">ロゴなし（名前がテキストで表示されます）</p>
              )}
              <input type="file" ref={fileInput} onChange={handleUploadInModal} accept="image/*" className="hidden" />
              <button
                onClick={() => fileInput.current?.click()}
                className="text-sm text-blue-600 hover:text-blue-800 font-bold"
              >
                {modalLogoUrl ? 'ロゴを変更' : 'ロゴをアップロード'}
              </button>
            </div>

            <div className="flex gap-3 justify-end">
              <button onClick={closeModal} className="bg-gray-200 text-gray-700 px-6 py-2 rounded-lg text-sm font-bold hover:bg-gray-300">キャンセル</button>
              <button onClick={saveModal} className="bg-blue-600 text-white px-6 py-2 rounded-lg text-sm font-bold hover:bg-blue-700">OK</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LogoAdmin;
